import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllPaymentsByAdmin, fetchPaymentsSummary } from '../../redux/actions/paymentActions';
import PaymentDetails from '../../components/payments/PaymentDetails';
import PaymentsSummary from '../../components/payments/PaymentsSummary';

const Payments = () => {
  const dispatch = useDispatch();
  const { payments = [], meta, loading, error, summary, summaryLoading } = useSelector((state) => state.payments || {});

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const debounceRef = useRef();

  const triggerFetch = (overrides = {}) => {
    dispatch(
      fetchAllPaymentsByAdmin({
        page,
        limit: 10,
        q: overrides.search ?? search,
        status: overrides.status ?? status,
        provider: overrides.provider ?? provider
      })
    );
    dispatch(fetchPaymentsSummary({
      q: overrides.search ?? search,
      status: overrides.status ?? status,
      provider: overrides.provider ?? provider
    }));
  };

  useEffect(() => {
    triggerFetch();
  }, [page]);

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      triggerFetch({ search: value, status, provider });
    }, 300);
  };

  const onStatusChange = (e) => {
    const value = e.target.value;
    setStatus(value);
    setPage(1);
    triggerFetch({ status: value });
  };

  const onProviderChange = (e) => {
    const value = e.target.value;
    setProvider(value);
    setPage(1);
    triggerFetch({ provider: value });
  };

  const handleRowClick = (payment) => {
    setSelectedPayment(payment);
    setShowDetails(true);
  };

  const handleCloseModal = () => {
    setShowDetails(false);
    setSelectedPayment(null);
  };

  return (
    <div className="container py-3">
      <h2 className="mb-3">Payments</h2>

      <PaymentsSummary summary={summary} loading={summaryLoading} />

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search by payer, payee, property, unit"
            value={search}
            onChange={onSearchChange}
          />
        </div>
        <div className="col-md-3">
          <select className="form-select" value={status} onChange={onStatusChange}>
            <option value="">All Status</option>
            <option value="CREATED">Created</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
            {/* <option value="REFUND">Refunded</option> */}
          </select>
        </div>
        <div className="col-md-3">
          <select className="form-select" value={provider} onChange={onProviderChange}>
            <option value="">All Providers</option>
            <option value="STRIPE">Stripe</option>
            <option value="PLAID">Plaid</option>
            <option value="IN_PERSON">Offline</option>
          </select>
        </div>
      </div>

      {/* {loading && <div>Loading...</div>} */}
      {error && <div className="text-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead className="table-primary">
            <tr>
              <th>Payee</th>
              <th>Payer</th>
              <th>Property</th>
              <th>Unit</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
             {loading && (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && payments.map((p) => (
              <tr
                key={p.id}
              >
                <td>{p.payment_receiver ? `${p.payment_receiver.first_name} ${p.payment_receiver.last_name}` : '-'}</td>
                <td>{p.payer ? `${p.payer.first_name} ${p.payer.last_name}` : '-'}</td>
                <td>{p.lease?.property?.property_name || '-'}</td>
                <td>{p.lease?.unit?.unit || '-'}</td>
                <td>${Number(p.amount || 0).toFixed(2)}</td>
                <td><span className="badge bg-info text-dark">{p.status}</span></td>
                <td>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleRowClick(p)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {!loading && payments.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-4">No payments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDetails && selectedPayment && (
        <PaymentDetails payment={selectedPayment} onClose={handleCloseModal} />
      )}

      {meta?.totalPages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-3">
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
          <span className="align-self-center">Page {page} of {meta.totalPages}</span>
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={page === meta.totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Payments;
